import { useContext, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "react-toastify"
// import { Eye, EyeOff, User, Mail, Lock, Phone } from "lucide-react"
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Person from '@mui/icons-material/Person';
import Email from '@mui/icons-material/Email';
import Lock from '@mui/icons-material/Lock';
import PhoneAndroid from '@mui/icons-material/PhoneAndroid';
// import Imagefile from 'assets/images/login.png';
import { AuthContext } from "provider/AuthProvider"
import { constants } from "lib/utilities/Auth"
import { signupSchema, techyrrAdminSignupSchema } from "lib/utilities/schemas/signupSchema"
import { addCompany } from "lib/utilities/company"
import { validateDomain } from "lib/utilities/urlUtilities"
import  CircleMatrix  from "examples/CircleMatrix" 
function Signup({ techyrrAdmin = false }) {
  const [credentials, setCredentials] = useState({
    fullName: "",
    gender: "",
    phone: "",
    email: "",
    password: "",
    company: techyrrAdmin ? "techyrr" : "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [creatingAccount, setCreatingAccount] = useState(false)

  const { signup, getCustomTokenFromBackend } = useContext(AuthContext)
  const { regex, errorCodes } = constants
  const navigate = useNavigate()
  const { origin } = useParams()

  const onChange = (e) => {
    const { name, value } = e.target
    let updatedValue = name === "phone" ? value.replace(regex.bannedSymbols.phoneNumber, "") : value
    setCredentials({ ...credentials, [name]: updatedValue })
  }

  const handleSignup = async (e) => {
    e.preventDefault()
    const schema = techyrrAdmin ? techyrrAdminSignupSchema : signupSchema
    const validationResult = schema.safeParse(credentials)

    if (!validationResult.success) {
      return toast.error(
        <p style={{ fontSize: "16px" }}>
          {validationResult.error.errors[0].message || "Try Again"}
        </p>
      )
    }

    try {
      setCreatingAccount(true)
      const res = await signup(
        credentials.fullName,
        credentials.gender,
        credentials.phone,
        null,
        credentials.email,
        credentials.password,
        techyrrAdmin && credentials.company,
        techyrrAdmin
      )

      const url = document.referrer
      if (validateDomain(url)) {
        const customToken = await getCustomTokenFromBackend(res.user.uid)
        if (techyrrAdmin) {
          await addCompany(credentials.company)
        }
        window.parent.postMessage({ token: customToken }, "*")
      }
    } catch (error) {
      toast.error(
        <p style={{ fontSize: "16px" }}>
          {errorCodes.firebase[error.code] || error.errorInfo?.message || "Try Again"}
        </p>
      )
    } finally {
      setCreatingAccount(false)
    }
  }

  const handleGotoSignIn = () => {
    if (origin) navigate(`/signin/${origin}`)
    else if (techyrrAdmin) navigate(`/techyrr/signin`)
    else navigate(`/signin`)
  }

  return (
    <div className="flex h-screen w-full">
      {/* Left Side - Form */}
      <div className="flex w-full items-center justify-center bg-white md:w-2/5">
        <div className="w-full max-w-md rounded-2xl bg-white p-10">
          <h1 className="mb-2 text-center text-4xl font-semibold text-gray-900">Create your account</h1>
          <p className="mb-6 text-center text-lg text-gray-500">Sign up to start your journey with us</p>

          <form className="space-y-5" onSubmit={handleSignup}>
            <TextInput icon={<Person />} placeholder="Full Name" name="fullName" value={credentials.fullName} onChange={onChange} />
            <TextInput icon={<Email />} placeholder="Email" type="email" name="email" value={credentials.email} onChange={onChange} />
            <TextInput icon={<PhoneAndroid />} placeholder="Phone" name="phone" value={credentials.phone} onChange={onChange} />
            <SelectInput icon={<Person />} name="gender" value={credentials.gender} onChange={onChange} />
            <PasswordInput show={showPassword} setShow={setShowPassword} name="password" value={credentials.password} onChange={onChange} placeholder="Password" />

            <button
              type="submit"
              disabled={creatingAccount}
              className="w-full rounded-xl bg-black py-3 text-lg font-semibold text-white transition-colors hover:bg-gray-900"
            >
              {creatingAccount ? "Signing Up..." : "Sign Up"}
            </button>
          </form>

          <p className="mt-8 text-center text-base text-gray-500">
            Already have an account?{" "}
            <span onClick={handleGotoSignIn} className="cursor-pointer font-medium text-black underline">
              Sign In
            </span>
          </p>
        </div>
      </div>

      <div className="relative hidden md:block md:w-3/5">
        <CircleMatrix />
      </div>
    </div>
  )
}

function TextInput({ icon, ...props }) {
  return (
    <div className="relative">
      <input
        {...props}
        className="w-full rounded-xl bg-gray-100 px-12 py-3 text-lg outline-none placeholder-gray-500 border-none"
      />
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>
    </div>
  )
}

function PasswordInput({ show, setShow, ...props }) {
  return (
    <div className="relative">
      <input
        {...props}
        type={show ? "text" : "password"}
        className="w-full rounded-xl bg-gray-100 px-12 py-3 text-lg outline-none placeholder-gray-500 border-none"
      />
      <Lock className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
      <button
        type="button"
        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400"
        onClick={() => setShow(!show)}
      >
        {show ? <VisibilityOff className="h-5 w-5" /> : <Visibility className="h-5 w-5" />}
      </button>
    </div>
  )
}

function SelectInput({ icon, name, value, onChange }) {
  return (
    <div className="relative">
      <select
        name={name}
        value={value}
        onChange={onChange}
        className="w-full appearance-none rounded-xl bg-gray-100 px-12 py-3 text-lg text-gray-700 outline-none"
      >
        <option value="">Select Gender</option>
        <option value="male">Male</option>
        <option value="female">Female</option>
        <option value="other">Other</option>
      </select>
      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">{icon}</span>
    </div>
  )
}

export default Signup
